'use client';

import Link from 'next/link';
import { useEffect } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import type { JWTUserData } from '@kit/supabase/types';
import { Trans } from '@kit/ui/trans';

import { UserAccountDropdown } from '~/(tenant)/_components/user-account-dropdown';
import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/book',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

interface TrsHeaderAccountSectionProps {
  scrolled?: boolean;
  onNavigate?: () => void;
  onAuthChange?: (isAuthenticated: boolean) => void;
  hidden?: boolean;
}

export function TrsHeaderAccountSection({
  scrolled = false,
  onNavigate,
  onAuthChange,
  hidden,
}: TrsHeaderAccountSectionProps) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  useEffect(() => {
    onAuthChange?.(!!session.data);
  }, [session.data, onAuthChange]);

  if (hidden) return null;

  if (session.data) {
    const role = userRole.data || 'guest';

    if (role === 'owner' || role === 'member') {
      return (
        <div className="animate-in fade-in flex items-center gap-2 duration-500">
          <Link
            href={
              teamAccount.data
                ? `/home/${teamAccount.data.slug}/dashboard`
                : '/home/settings'
            }
            onClick={onNavigate}
            className={`hidden md:inline-flex ml-2 px-5 py-2.5 rounded-full text-lg tracking-[0.1em] transition-all duration-300 ${
              scrolled
                ? 'bg-white text-[var(--trs-green)] hover:bg-white/90'
                : 'bg-white/90 text-[var(--trs-green)] hover:bg-white'
            }`}
          >
            <Trans i18nKey="common:adminPanel" defaults="Admin Panel" />
          </Link>

          <PersonalAccountDropdown
            showProfileName={false}
            paths={paths}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }

    if (role === 'user') {
      return (
        <div className="animate-in fade-in flex items-center gap-2 duration-500">
          <Link
            href="/book"
            onClick={onNavigate}
            className={`hidden md:inline-flex ml-2 px-5 py-2.5 rounded-full text-lg tracking-[0.1em] transition-all duration-300 ${
              scrolled
                ? 'bg-white text-[var(--trs-green)] hover:bg-white/90'
                : 'bg-white/90 text-[var(--trs-green)] hover:bg-white'
            }`}
          >
            <Trans i18nKey="common:bookCourt" defaults="Book Now" />
          </Link>

          <UserAccountDropdown
            showProfileName={false}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }
  }

  // Guest users - auth button is in the navigation pill
  return (
    <div className="animate-in fade-in flex items-center gap-2 duration-500">
      {/* Use <a> tag for hard navigation to avoid hydration mismatch between route groups */}
      <a
        href={pathsConfig.auth.signIn}
        onClick={onNavigate}
        className={`inline-flex items-center justify-center px-6 py-2.5 rounded-full text-lg tracking-[0.1em] transition-all duration-300 ${
          scrolled
            ? 'bg-white text-[var(--trs-green)] hover:bg-white/90'
            : 'bg-white/90 text-[var(--trs-green)] hover:bg-white'
        }`}
      >
        <Trans i18nKey="auth:signIn" defaults="Sign In" />
      </a>
    </div>
  );
}

function useSession() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data: listener } = client.auth.onAuthStateChange(() => {
      queryClient.invalidateQueries({ queryKey: ['session'] });
      queryClient.invalidateQueries({ queryKey: ['user-role'] });
      queryClient.invalidateQueries({ queryKey: ['team-account'] });
    });

    return () => listener.subscription.unsubscribe();
  }, [client.auth, queryClient]);

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
}

function useUserRole(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['user-role', userId],
    queryFn: async () => {
      if (!userId) return 'guest';

      try {
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select('account_role')
          .eq('user_id', userId);

        if (memberships && memberships.length > 0) {
          if (memberships.some((m) => m.account_role === 'owner')) {
            return 'owner';
          }
          return 'member';
        }

        return 'user';
      } catch {
        return 'guest';
      }
    },
    enabled: !!userId,
  });
}

function useTeamAccount(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['team-account', userId],
    queryFn: async () => {
      if (!userId) return null;

      try {
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select(
            `
            account_id,
            accounts!accounts_memberships_account_id_fkey!inner (
              id,
              slug,
              name,
              is_personal_account
            )
          `,
          )
          .eq('user_id', userId)
          .eq('accounts.is_personal_account', false);

        if (memberships && memberships.length > 0) {
          return memberships[0]?.accounts;
        }

        return null;
      } catch {
        return null;
      }
    },
    enabled: !!userId,
  });
}
